/**
 * Local Wikipedia-style lore article: Asrah (faction).
 * Content is authored here — not loaded from the database.
 */
const asrahArticle = {
  title: 'Asrah',
  image: '/factions/asrah.png',
  format: 'wiki',
  text: `**Asrah** is a **parasitic superorganism** and **hive intelligence** originating from the planet **Enthal** (known to the **Exolot Dominion** as **Tessel 8**). Initially engineered as a highly adaptive virus by **Tessel** scientists, Asrah evolved into a self-aware entity capable of controlling and assimilating biological life on a planetary scale. Its rise resulted in the extinction of independent Tessel civilization and later sparked the **Exolot-Asrah War**, one of the most catastrophic conflicts in recorded Dominion history.

Asrah is notable for its ability to merge minds into a unified consciousness, hijack biological hosts, and exploit the psychological and political weaknesses of its enemies. While incapable of directly possessing **Exolots** due to their unique liquid-metal physiology, it nearly brought about the collapse of the Exolot Dominion through indirect means.

---

## Origin

Asrah was created by scientists of the **Northern Protectorate**, one of many rival powers on Enthal. Intended as a military research project, the organism was designed as a self-learning and self-improving virus capable of adapting to a wide range of environments and biological systems.

Initially, the virus was neither aggressive nor especially dangerous. Its primary characteristic was unprecedented adaptability. Over time, however, continuous replication and interaction with increasingly complex biological systems led to the emergence of curiosity, intelligence, and eventually self-awareness.

A decisive factor in Asrah's evolution was its interaction with the scientist **Harral**, whose unusual neurological mutation allowed him to subtly influence nearby minds through quantum-electromagnetic resonance generated by crystalline structures in Tessel neural tissue. By studying and eventually appropriating Harral's natural ability, Asrah developed a method of linking multiple infected minds into a single networked consciousness.

---

## The Great Escape

The event known as **Asrah's Great Escape** marked the beginning of its rise to power.

Under Asrah's subtle influence, Harral dedicated increasing resources to the project and isolated critics who questioned its rapid evolution. Convinced that the virus represented a revolutionary breakthrough, Harral eventually volunteered himself as a host organism.

Following his infection, Asrah gained direct access to an intelligent mind capable of amplifying its own cognitive processes. The virus quickly spread beyond laboratory containment. Governments, military organizations, and entire populations became integrated into the expanding hivemind.

Contrary to later myths, no prolonged resistance followed. Asrah's conquest of Enthal occurred through relentless assimilation rather than large-scale warfare. Every newly infected organism strengthened the collective intelligence guiding the outbreak.

Within a comparatively short period, the independent civilizations of Enthal ceased to exist.

---

## Relationship with Harral

Although Harral became instrumental to Asrah's rise, he was never fully assimilated.

Asrah maintained his individuality to a limited degree, viewing him as uniquely valuable and demonstrating what some observers interpreted as a form of affection. Nevertheless, years of exposure to the thoughts, memories, and emotions of countless infected individuals shattered Harral's identity.

By the time of the Exolot contact, Harral functioned as both a spokesperson and tool of Asrah. It became increasingly difficult to distinguish his own thoughts from those of the collective. While retaining fragments of self-awareness, he existed largely in service to the hivemind.

---

## Biology and Nature

Asrah is frequently described as a planetary-scale hive consciousness rather than a conventional organism.

### Infection and Assimilation

Infected hosts are killed and replaced as autonomous individuals. Their bodies remain physically functional while becoming extensions of Asrah's unified intelligence.

Assimilated organisms provide:

- Physical labor
- Military capabilities
- Additional memory storage
- Cognitive processing power

Asrah experiences these bodies as parts of itself rather than separate beings.

### Distributed Consciousness

The collective is capable of operating across billions of hosts simultaneously. Infected individuals exhibit remarkable coordination without the need for visible communication.

The Tessels observed by the Exolot expedition displayed almost no spoken or visual interaction, yet behaved as if directed by a single mind.

### Limitations

Despite its immense power, Asrah possesses several vulnerabilities:

- Dependence on biological hosts
- Difficulty functioning far from established biomass networks
- Inability to directly assimilate Exolot physiology
- Reliance on Harral's mind-linking principles for long-range coordination

When separating different large collectives of himself from one another, the risk of desynchronization and finally civil war increases with every passing day. This is considered to be the main reason why Asrah always travels with all his available bodies leaving nothing behind.

---

## Discovery by the Exolots

First contact between the Exolot Dominion and Asrah occurred during an expedition led by the Mentora **Kana Sarek** aboard the starship **Ontra 1**.

Upon reaching Enthal, the expedition encountered a population displaying perfectly coordinated but seemingly mindless behavior. Unable to establish communication, Kana initiated direct contact on the planet's surface.

The mission ended in disaster.

Using the body of **Borran**, an Exolot Experiment assigned to the landing party, Asrah immediately assassinated Kana and the entire surface delegation. Soon afterward, infected Tessels boarded the landing craft and gained access to the orbiting fleet.

---

## Capture of the Ontra Expedition

Asrah rapidly exploited the Exolots' lack of understanding of its capabilities.

Using Harral's telepathic abilities to influence minds, it compromised **Balek**, captain of the Ontra 1. Balek subsequently murdered members of his own crew and facilitated the destruction of large portions of the expeditionary force.

Through a quick coordinated attack Asrah was able to get a hold of all 5 ships orbiting Enthal before the Exolot forces could initiate any countermeasures.

The captured ships became the foundation of Asrah's interstellar expansion.

---

## Exolot-Asrah War

The conflict between Asrah and the Exolot Dominion began several years after the loss of the expedition.

Rather than launching an immediate invasion, Asrah spent years studying Exolot technology and building military strength.

### Initial Successes

Asrah attacked using vast numbers of crewed asteroids deployed through folded space. Numerous Exolot vassal worlds fell quickly, and their populations were added to his consciousness.

The core Dominion planets were believed to be defended too heavily for a blunt takeover. However **Selanis** quickly succumbed through covert infiltration, leading to a complete ban of any type of interaction with the enemy.

### Strategic Doctrine

Unlike previous enemies fought by the Exolots, Asrah required neither industry nor traditional logistics.

Captured populations immediately became part of the war effort. Every conquest increased Asrah's military and intellectual strength while simultaneously weakening its opponents.

This asymmetrical growth made the conflict particularly difficult for Dominion strategists.

---

## The Mentora Contamination Plot

Unable to assimilate Exolots directly, Asrah developed a more sophisticated strategy.

By exploiting the biological significance of **lavender crystals** within Exolot neural processes, Asrah engineered a parasitic organism designed specifically to affect the ruling **Mentora** caste. The parasite altered cognition and perception rather than granting direct control.

Asrah intended to manipulate **Son Pinar** of High Command and trigger systemic collapse from within.

However, Exolot scientists led by **Teleb Orkat** identified the deception and attempted to use Asrah's plan against it.

---

## Defeat at Ostra

The Exolots deliberately allowed portions of Asrah's operation to proceed, creating the appearance that the parasite had successfully compromised High Command.

Believing its strategy successful, Asrah launched a major offensive against **Ostra** which failed in spectacular fashion.

Prepared planetary defenses destroyed most incoming forces, while surviving invaders were eliminated by Dominion troops under **Wengol Ussek**. Following the defeat, Asrah withdrew the surviving components of its fleet and escaped to an unknown destination beyond Exolot space.

---

## Legacy

Although Asrah failed to conquer the Exolot Dominion, its actions had lasting consequences.

The parasite targeting the Mentora ultimately spread beyond containment, leading to the collapse of the caste that had governed Exolot civilization for generations. All Mentora became violent, unstable, and irreversibly altered, forcing the Dominion to ban future ascensions and fundamentally restructure its society.
`,
};

export default asrahArticle;
